import { type ComponentProps, useCallback, useState } from "react";
import { useSWRPostData } from "@root/shared/hooks/useSWRPostData";
import { useEntryLink } from "@userFrontend/hooks/useEntryLink";
import { useSalonTourButton } from "@userFrontend/hooks/useSalonTourButton";
import { useExtendScoutDeadlineButton } from "@userFrontend/hooks/useExtendScoutDeadlineButton";
import { useLineConsultButton } from "@userFrontend/hooks/useLineConsultButton";
import { addQueryStrings } from "@userFrontend/utils/addQueryStrings";
import { AGENT_LINE_URL } from "@root/shared/utils/constants";
import { type Presentation } from "./Presentation";
import { type FetchedData } from "./type";

type UseWorkDetailBottomButtonsParams = {
  fetchUrl: string;
  fetchBody: Record<string, unknown>;
  recommendFetchUrl: string;
  createLineConsultButtonHistoryUrl: string;
  isShowLineConsultButton: boolean;
};

export const useWorkDetailBottomButtons = ({
  fetchUrl,
  fetchBody,
  recommendFetchUrl,
  createLineConsultButtonHistoryUrl,
  isShowLineConsultButton,
}: UseWorkDetailBottomButtonsParams): ComponentProps<typeof Presentation> | undefined => {
  const gaPrefix = "detail";
  const position = "bottom";

  const [isShowAfterKeepRecommend, setIsShowAfterKeepRecommend] = useState(false);

  const { handleClickEntryLink } = useEntryLink();
  const { handleClickSalonTourButton } = useSalonTourButton();
  const { handleClickExtendScoutDeadlineButton } = useExtendScoutDeadlineButton();
  const { handleClickLineConsultButton } = useLineConsultButton();

  const handleClickKeepForm = useCallback(
    (event: React.MouseEvent<HTMLElement>, isApp: boolean) => {
      // すでにキープ押下後のレコメンドが表示済み あるいは アプリwebview
      if (isShowAfterKeepRecommend || isApp) return;

      // キープ押下後レコメンド表示時にスクロールする位置を取るため
      const element = document
        .getElementById("after-keep-recommend-portal")
        ?.getBoundingClientRect();
      if (element) {
        const TAB_HEIGHT = 56;
        const scrollPosition = element.top + window.scrollY - TAB_HEIGHT;
        // キープボタンは"1"、解除ボタンは"2"
        const keepButtonStatus = event.currentTarget
          ?.closest("[data-bookmark-status]")
          ?.getAttribute("data-bookmark-status");

        // キープ押下時のみ表示し、スクロールする
        if (keepButtonStatus === "1") {
          setIsShowAfterKeepRecommend(true);
          window.scrollTo({
            top: scrollPosition,
            behavior: "smooth",
          });
        }
      }
    },
    [isShowAfterKeepRecommend],
  );

  const createData = useCallback(
    (fetchedData: FetchedData) => {
      const entryUrlWithQuery = addQueryStrings(
        fetchedData.buttonContents.entryButtonContent.entryUrl,
        fetchedData.queryStr,
      );

      const salonTourEntryUrlWithQuery = addQueryStrings(
        fetchedData.buttonContents.salonTourButtonContent.salonTourEntryUrl,
        fetchedData.queryStr,
      );

      // APPの場合は何もパラメータを付けない
      const salonTourButtonUrl = fetchedData.isApp
        ? fetchedData.buttonContents.salonTourButtonContent.salonTourEntryUrl
        : salonTourEntryUrlWithQuery;

      return {
        recommendFetchUrl,
        isShowAfterKeepRecommend,
        queryStr: fetchedData.queryStr,
        workCode: fetchedData.workCode,
        clientShopId: fetchedData.clientShopId,
        capitalizePageType: fetchedData.capitalizePageType,
        isPreview: fetchedData.isPreview,
        isSp: fetchedData.isSp,
        isApp: fetchedData.isApp,
        entryProps: {
          isEnterable: fetchedData.buttonContents.entryButtonContent.isEnterable,
          hasEntried: fetchedData.buttonContents.entryButtonContent.hasEntried,
          firebaseAction: "entry",
          /* eslint no-script-url: 0 */
          url: fetchedData.isPreview ? "javascript:void(0)" : entryUrlWithQuery,
          gaAction: fetchedData.buttonContents.scoutButtonContent.isScouted
            ? `${gaPrefix}_scout_entry${position}`
            : `${gaPrefix}_entry${position}`,
          gaLabel: fetchedData.gaLabel,
          onClick: () => handleClickEntryLink(),
        },
        keepProps: {
          hasKeeped: fetchedData.buttonContents.keepButtonContent.hasKeeped,
          isShowKeepBalloon: false, // 求人詳細下部ボタンでは一律で表示しないため
          isShowConfirmRemoveKeepModal:
            fetchedData.buttonContents.keepButtonContent.isShowConfirmRemoveKeepModal,
          queryStr: `client_shop_id=${fetchedData.clientShopId}`,
          /* eslint no-script-url: 0 */
          url: fetchedData.isPreview
            ? "javascript:void(0)"
            : fetchedData.buttonContents.keepButtonContent.keepButtonUrl,
          gaAction: `${gaPrefix}_keep-${position}`,
          gaLabel: fetchedData.gaLabel,
          onClick: (event: React.MouseEvent<HTMLElement>) =>
            fetchedData.isPreview ? undefined : handleClickKeepForm(event, fetchedData.isApp),
        },
        salonTourProps: {
          isSalonTour: fetchedData.buttonContents.salonTourButtonContent.isSalonTour,
          /* eslint no-script-url: 0 */
          url: fetchedData.isPreview ? "javascript:void(0)" : salonTourButtonUrl,
          onClick: () =>
            handleClickSalonTourButton({
              isApp: fetchedData.isApp,
              workCode: fetchedData.workCode,
              isScouted: fetchedData.buttonContents.scoutButtonContent.isScouted,
              clientShopId: fetchedData.clientShopId,
              salonTourEntryUrl:
                fetchedData.buttonContents.salonTourButtonContent.salonTourEntryUrl,
            }),
        },
        telEntryProps: {
          isShowTelButton: fetchedData.telNumberContent.isShowTellButton,
          telNumber: fetchedData.telNumberContent.telNumber,
          gaLabel: fetchedData.gaLabel,
        },
        scoutProps: {
          isScouted: fetchedData.buttonContents.scoutButtonContent.isScouted,
          scoutClientShopId: fetchedData.buttonContents.scoutButtonContent.scoutClientShopId,
          url: fetchedData.buttonContents.scoutButtonContent.scoutedEntryUrl,
        },
        extendScoutProps: {
          isScoutExtend: fetchedData.buttonContents.scoutButtonContent.isScoutExtend,
          scoutDeadline: fetchedData.buttonContents.scoutButtonContent.scoutDeadline,
          url: fetchedData.buttonContents.scoutButtonContent.extendScoutUrl,
          gaAction: `${gaPrefix}_scout_extend-${position}`,
          gaLabel: fetchedData.gaLabel,
          onClick: () =>
            handleClickExtendScoutDeadlineButton(
              fetchedData.buttonContents.scoutButtonContent.extendScoutUrl || "",
            ),
        },
        declineScoutProps: {
          url: fetchedData.buttonContents.scoutButtonContent.declineScoutUrl,
          gaAction: `${gaPrefix}_scout_decline-${position}`,
          gaLabel: fetchedData.gaLabel,
        },
        lineProps: {
          isShowLineConsultButton,
          /* eslint no-script-url: 0 */
          lineUrl: fetchedData.isPreview ? "javascript:void(0)" : AGENT_LINE_URL,
          fetchUrl: fetchedData.isPreview ? undefined : createLineConsultButtonHistoryUrl,
          gaAction: `${gaPrefix}_line-${position}`,
          onClick: () =>
            fetchedData.isPreview
              ? undefined
              : handleClickLineConsultButton({
                  workCode: fetchedData.workCode,
                  lineUrl: AGENT_LINE_URL,
                  createHistoryUrl: createLineConsultButtonHistoryUrl,
                  clientShopId: fetchedData.clientShopId,
                }),
        },
      };
    },
    [
      recommendFetchUrl,
      isShowAfterKeepRecommend,
      handleClickKeepForm,
      handleClickEntryLink,
      handleClickSalonTourButton,
      handleClickExtendScoutDeadlineButton,
      isShowLineConsultButton,
      createLineConsultButtonHistoryUrl,
      handleClickLineConsultButton,
    ],
  );

  const data = useSWRPostData<{ data: FetchedData }>(fetchUrl, fetchBody);

  // サーバ側で例外が起きると HTTP 200 のまま data が null で返ることがあるため、
  // 期待する形でなければ何も表示しない
  const formattedData = data?.data?.buttonContents ? createData(data.data) : undefined;

  return formattedData;
};
